import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import debounce from 'lodash/debounce'
import get from 'lodash/get'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import NumberInput from '@react/react-spectrum/NumberInput'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Select from '@react/react-spectrum/Select'
import Slider from '@react/react-spectrum/Slider'
import Switch from '@react/react-spectrum/Switch'
import Textarea from '@react/react-spectrum/Textarea'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import Help from '@react/react-spectrum/Icon/Help'

import ColorInput from 'components/ColorInput'
import DxaSurveyPicker from 'components/DxaSurveyPicker'
import DatastreamPicker from 'components/DatastreamPicker'
import { FormEditorInput } from 'components/FormEditor'
import IconPicker from 'components/IconPicker'
import MediaUpload from 'components/MediaUpload'
import Multifield from 'components/Multifield'
import { FormTableInput } from 'components/FormTable'
import PlainTextEditor from 'components/PlainTextEditor'
import ProductPicker from 'components/ProductPicker'
import { ProcessEditorInput } from 'components/ProcessEditor'
import Radio from '@react/react-spectrum/Radio'
import RadioGroup from '@react/react-spectrum/RadioGroup'
import { RichTextFormField } from 'components/RichTextEditor'
import { ScoreEditorInput } from 'components/ScoreEditor'
import ScreenPicker from 'components/ScreenPicker'
import { ShippingOptionEditorInput } from 'components/ShippingOptionEditor'
import { useScreenBuilderProjectContext } from 'views/Project/context/ScreenBuilderProjectContext'

function ComponentField({ field, component, config, disabled, onSave, projectType }) {
  const shouldSaveToMeta = config?.hasChildren || config?.hasColumns || field?.isMeta
  const { projectId, screenName } = useParams()
  const componentIdRef = useRef('')
  const projectContext = useScreenBuilderProjectContext()
  const [value, setValue] = useState(
    shouldSaveToMeta ? get(component.meta, field['name']) : get(component.content, field['name'])
  )
  const debouncedSave = useCallback(debounce(onSave(field['name']), 1000), [onSave, field])

  const onChange = (v) => {
    setValue(v)
    onSave(field['name'])(v)
  }

  const debouncedOnChange = (v) => {
    setValue(v)
    debouncedSave(v)
  }

  useEffect(() => {
    if (component?.meta?.id && component.meta.id !== componentIdRef.current) {
      componentIdRef.current = component.meta.id
      setValue(shouldSaveToMeta ? get(component.meta, field['name']) : get(component.content, field['name']))
    }
  }, [component, field, shouldSaveToMeta])

  let attrs = {},
    _options = []
  const _value = value || field['defaultValue']

  // extended Array.filter with component as a second parameter
  const filterArray = (items) => {
    if (Array.isArray(items)) {
      if (typeof field['filter'] === 'function') {
        return items.filter((item) => field['filter'](item, component))
      }
      return items
    }
    return []
  }

  switch (field?.type) {
    case 'color':
      return (
        <ColorInput
          placeholder={field['label']}
          id={'component-sidebar-' + field['name']}
          value={value || ''}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'dropdown':
      // Handle both static options and dynamic getOptions function
      if (typeof field['getOptions'] === 'function') {
        _options = field['getOptions'](component, projectContext)
      } else {
        _options = filterArray(field['options'])
      }

      return (
        <Select
          placeholder={field['label']}
          id={'component-sidebar-' + field['name']}
          value={
            value !== undefined && value !== null && value !== ''
              ? value
              : field['defaultValue'] != null
                ? field['defaultValue']
                : ''
          }
          options={_options}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'form':
      return <FormEditorInput label={field['label']} fields={value || []} onChange={onChange} />
    case 'icon':
      return (
        <IconPicker
          placeholder={field['label']}
          id={'component-sidebar-' + field['name']}
          value={value || ''}
          onChange={onChange}
          disabled={disabled}
          isMobileV2={projectType === 'mobile-v2'}
        />
      )
    case 'image':
      return (
        <MediaUpload
          type='image'
          value={value || ''}
          maxHeight='100px'
          onUpload={onChange}
          projectId={projectId}
          path={`${screenName ?? ''}-cmp-${component.meta.id}`}
          withTextfield={true}
          disabled={disabled}
        />
      )
    case 'video':
      return (
        <MediaUpload
          type='video'
          value={value || ''}
          maxHeight='100px'
          onUpload={onChange}
          projectId={projectId}
          path={`${screenName ?? ''}-cmp-${component.meta.id}`}
          withTextfield={true}
          disabled={disabled}
        />
      )
    case 'multiPicker':
      _options = filterArray(field['options'])
      return (
        <Multifield
          type='dropdown'
          id={'component-sidebar-' + field['name']}
          value={value || ''}
          options={_options}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'number':
      return (
        <NumberInput
          value={value}
          min={field['min'] || 0}
          max={field['max'] || 30}
          step={field['step'] || 1}
          onChange={debouncedOnChange}
          disabled={disabled}
        />
      )
    case 'switch':
      return <Switch label={field['label']} checked={!!value} onChange={(v) => !disabled && onChange(v)} />
    case 'radio':
      _options = filterArray(field['options'])
      return (
        <RadioGroup id={'component-sidebar-' + field['name']} onChange={onChange} disabled={disabled}>
          {_options.map((opt) => (
            <Radio key={opt.value} checked={opt.value === _value} {...opt} />
          ))}
        </RadioGroup>
      )
    case 'richText':
      return <RichTextFormField label={field['label']} value={value || ''} onChange={onChange} disabled={disabled} />
    case 'plainTextEditor':
      return (
        <PlainTextEditor label={field['label']} defaultValue={value || ''} onChange={onChange} disabled={disabled} />
      )
    case 'products':
      attrs = {}
      if (field['multiple'] === true) attrs['multiple'] = true
      if (field['filter'] === true) attrs['filter'] = field['filter']

      return (
        <ProductPicker
          value={value}
          currentScreen={screenName}
          onChange={onChange}
          disabled={disabled}
          emptyOptionLabel={field['emptyOptionLabel']}
          {...attrs}
        />
      )
    case 'screens':
      attrs = {}
      if (field['multiple'] === true) attrs['multiple'] = true
      if (field['filter']) attrs['filter'] = field['filter']
      return (
        <ScreenPicker
          value={value}
          currentScreen={screenName}
          id={component.meta.id}
          onChange={onChange}
          disabled={disabled}
          allowExternal={field['allowExternal']}
          allowProduct={field['allowProduct']}
          preset={field['preset']}
          {...attrs}
        />
      )
    case 'shippingOptions':
      return (
        <ShippingOptionEditorInput
          label={field['label']}
          defaultValue={field['defaultValue']}
          fields={value || []}
          onChange={onChange}
          disabled={disabled}
        />
      )
    case 'slider':
      return (
        <Slider
          value={value}
          min={field['min'] || 0}
          max={field['max'] || 30}
          step={field['step']}
          renderLabel
          label={field['label']}
          onChange={debouncedOnChange}
          disabled={disabled}
          getAriaValueText={field['displayAs'] === 'percentage' ? (v) => `${v * 100}%` : undefined}
        />
      )
    case 'textarea':
      return (
        <Textarea
          placeholder={field['label']}
          id={'component-sidebar-' + field['name']}
          value={value || ''}
          onChange={debouncedOnChange}
          disabled={disabled}
        />
      )
    case 'scoreEditor':
      return <ScoreEditorInput label={field['label']} fields={value || []} onChange={onChange} disabled={disabled} />
    case 'optionEditor':
      return (
        <FormTableInput
          label={field['label']}
          columns={field['columns']}
          saveAs={field['saveAs']}
          fields={value || []}
          onChange={onChange}
          disabled={disabled}
          projectType={projectType}
        />
      )
    case 'processEditor': {
      const processColumns = typeof field['columns'] === 'function' ? field['columns'](component) : field['columns']
      return (
        <ProcessEditorInput
          label={field['label']}
          fields={value || []}
          onChange={onChange}
          disabled={disabled}
          columns={processColumns}
          emptyRow={field['emptyRow']}
        />
      )
    }
    case 'multiField':
      return <Multifield value={value || []} onChange={onChange} disabled={disabled} />
    case 'custom':
      return !field.isComponent ? (
        <div dangerouslySetInnerHTML={{ __html: field.render({ component, projectId }) }} />
      ) : (
        field.render({ component, projectId })
      )
    case 'dxaSurvey':
      return <DxaSurveyPicker value={value || ''} onChange={onChange} disabled={disabled} />
    case 'datastream':
      return <DatastreamPicker value={value || ''} onChange={onChange} disabled={false} />
    case 'textfield':
    default:
      return (
        <Textfield
          placeholder={field['label']}
          id={'component-sidebar-' + field['name']}
          value={value || ''}
          onChange={debouncedOnChange}
          disabled={disabled}
        />
      )
  }
}

function ComponentFieldWrapper({ field, component, config, idx, disabled, onSave, projectType }) {
  return (
    <div className='ComponentField'>
      <FieldLabel
        label={['slider', 'switch', 'custom'].includes(field['type']) ? '' : field['label']}
        labelFor={'component-sidebar-' + field['name']}
      >
        <ComponentField
          projectType={projectType}
          field={field}
          component={component}
          config={config}
          idx={idx}
          onSave={onSave}
          disabled={disabled}
        />
      </FieldLabel>
      {field['tooltip'] && (
        <OverlayTrigger placement='right'>
          <Button className='tooltip-btn' variant='tool' icon={<Help size='XS' />} />
          <Tooltip variant='info'>{field['tooltip']}</Tooltip>
        </OverlayTrigger>
      )}
    </div>
  )
}

export default ComponentFieldWrapper
